import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

export type TypeDocument =
  | 'CERFA'
  | 'CONVENTION_FORMATION'
  | 'PROGRAMME_FORMATION'
  | 'CALENDRIER_FORMATION'
  | 'CERTIFICAT'
  | 'PAGE_GARDE';

export type StatutDocument = 'GENERE' | 'VERIFIE' | 'VALIDE' | 'TRANSMIS';

/** État de l'exemplaire signé déposé en retour du document produit. */
export type StatutDocumentSigne = 'DEPOSE' | 'VALIDE' | 'REFUSE';

/**
 * Exemplaire signé d'un document produit, déposé en retour.
 *
 * Ce n'est pas un document de plus : c'est un état du document produit, avec
 * son propre contrôle. Le chemin de stockage n'en fait jamais partie — le
 * fichier ne sort que par la route gardée.
 */
export interface ExemplaireSigne {
  id: string;
  nomFichier: string;
  tailleOctets: number;
  statut: StatutDocumentSigne;
  deposeLe: string;
  controleLe: string | null;
  motifRefus: string | null;
}

/**
 * Suivi Vérifié / Validé / Transmis d'un document (CDC §6.4). Chaque étape
 * franchie porte sa date et son auteur ; `etapes` dit ce qui reste possible —
 * le suivi ne revient pas en arrière, l'écran n'a rien à recalculer.
 */
export interface SuiviDocument {
  statut: StatutDocument;
  verifieLe: string | null;
  verifiePar: string | null;
  valideLe: string | null;
  validePar: string | null;
  transmisLe: string | null;
  transmisPar: string | null;
  destinataire: string | null;
  etapes: StatutDocument[];
}

export interface DocumentGenere {
  id: string;
  type: TypeDocument;
  statut: StatutDocument;
  suivi: SuiviDocument;
  modele: string | null;
  genereLe: string;
  transmisLe: string | null;
  destinataire: string | null;
  generePar: string | null;
  signe: ExemplaireSigne | null;
  /** Faux pour un document qui ne se signe pas (calendrier de formation). */
  signable?: boolean;
}

export interface ReponseDocuments {
  mode: 'CFA' | 'OF';
  /** Session rattachée au dossier ; `null` tant qu'il n'y en a pas. */
  session: string | null;
  modeleCerfaSuggere: string;
  documents: DocumentGenere[];
}

export function useDocuments(apprenantId: string | undefined) {
  return useQuery({
    queryKey: ['documents', apprenantId],
    queryFn: () => appelApi<ReponseDocuments>(`/apprenants/${apprenantId}/documents`),
    enabled: Boolean(apprenantId),
  });
}

export interface ResultatGeneration {
  documentId: string;
  type: TypeDocument;
  intitule: string;
  avertissements: string[];
}

export function useGenererDocument(apprenantId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { type: TypeDocument; modeleCerfa?: string }) =>
      appelApi<ResultatGeneration>(`/apprenants/${apprenantId}/documents`, {
        methode: 'POST',
        corps: variables,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['documents', apprenantId] });
      void client.invalidateQueries({ queryKey: ['dossier', apprenantId] });
    },
  });
}

export function useImporterDocument(apprenantId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { type: TypeDocument; fichier: File }) => {
      const formulaire = new FormData();
      formulaire.append('type', variables.type);
      formulaire.append('fichier', variables.fichier);
      return appelApi<ResultatGeneration>(`/apprenants/${apprenantId}/documents/importer`, {
        methode: 'POST',
        formulaire,
      });
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['documents', apprenantId] });
      void client.invalidateQueries({ queryKey: ['dossier', apprenantId] });
    },
  });
}

/** Étape du suivi demandée : ce que la règle de l'API autorisera ou non. */
export interface DemandeSuivi {
  documentId: string;
  statut: Exclude<StatutDocument, 'GENERE'>;
  destinataire?: string;
}

/** Les trois statuts de suivi documentaire du CDC §6.4, côté pôle administratif. */
export function useChangerStatutDocument(apprenantId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: DemandeSuivi) =>
      appelApi<{ message: string; suivi: SuiviDocument }>(`/documents/${variables.documentId}`, {
        methode: 'PATCH',
        corps: { statut: variables.statut, destinataire: variables.destinataire },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['documents', apprenantId] });
      void client.invalidateQueries({ queryKey: ['dossier', apprenantId] });
    },
  });
}

/**
 * Dépôt de l'exemplaire signé d'un document produit.
 *
 * `apprenantId` ne sert qu'à rafraîchir les deux écrans qui affichent le
 * document : la route, elle, ne connaît que le document — l'API en déduit le
 * dossier et vérifie le périmètre de sites.
 */
export function useDeposerDocumentSigne(apprenantId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { documentId: string; fichier: File }) => {
      const formulaire = new FormData();
      formulaire.append('fichier', variables.fichier);
      return appelApi<ExemplaireSigne>(`/documents/${variables.documentId}/signe`, {
        methode: 'POST',
        formulaire,
      });
    },
    onSuccess: () => rafraichirDocuments(client, apprenantId),
  });
}

/** Contrôle de l'exemplaire signé : validé, ou refusé avec son motif. */
export function useControlerDocumentSigne(apprenantId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      documentId: string;
      statut: 'VALIDE' | 'REFUSE';
      motifRefus?: string;
    }) =>
      appelApi<ExemplaireSigne>(`/documents/${variables.documentId}/signe`, {
        methode: 'PATCH',
        corps: { statut: variables.statut, motifRefus: variables.motifRefus },
      }),
    onSuccess: () => rafraichirDocuments(client, apprenantId),
  });
}

/** Retrait d'un exemplaire déposé par erreur. L'API le refuse une fois validé. */
export function useRetirerDocumentSigne(apprenantId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { documentId: string }) =>
      appelApi<{ message: string }>(`/documents/${variables.documentId}/signe`, {
        methode: 'DELETE',
      }),
    onSuccess: () => rafraichirDocuments(client, apprenantId),
  });
}

function rafraichirDocuments(
  client: ReturnType<typeof useQueryClient>,
  apprenantId: string | undefined,
): void {
  void client.invalidateQueries({ queryKey: ['documents', apprenantId] });
  void client.invalidateQueries({ queryKey: ['dossier', apprenantId] });
}

/** Référentiels exposés par l'API : formations, modèles de CERFA, pièces, barèmes. */
export interface Referentiels {
  formations: {
    code: string;
    titre: string;
    niveau: string;
    /** Blocs du programme ; `id` présent quand la formation est installée en base. */
    modules?: { id?: string; libelle: string }[];
  }[];
  modelesCerfa: {
    code: string;
    libelle: string;
    formationCode?: string | null;
    sites: string[];
  }[];
  /** Nomenclatures du bloc « L'apprenti(e) » du CERFA : codes, pas libellés. */
  cerfaApprenti: {
    regimesSociaux: EntreeNomenclature[];
    situationsAvantContrat: EntreeNomenclature[];
    dernieresClasses: EntreeNomenclature[];
    diplomes: EntreeNomenclature[];
  };
  /** Codes postaux de La Réunion et commune desservie (974 uniquement). */
  communesReunion: CommunePostale[];
  colonnesSifa: string[];
  bpf: {
    /** Version du formulaire dont les intitulés sont repris, ex. « Cerfa n° 10443*17 ». */
    version: string;
    cadres: Record<string, string>;
    rubriques: RubriqueBpf[];
  };
}

/** Un code postal réunionnais et la commune qu'il dessert. */
export interface CommunePostale {
  codePostal: string;
  commune: string;
  /** Bureau distributeur, quand il diffère du nom de la commune. */
  localite?: string;
}

/** Entrée d'une nomenclature officielle : le code part sur le formulaire. */
export interface EntreeNomenclature {
  code: string;
  libelle: string;
  /** « Aucun diplôme » : le dossier n'attend alors pas de copie de diplôme. */
  sansDiplome?: boolean;
}

/** Une rubrique du formulaire BPF, telle que déclarée par le référentiel API. */
export interface RubriqueBpf {
  /** Clé technique : sert de clé React, jamais affichée. */
  cle: string;
  cadre: string;
  /** Numéro ou lettre de la ligne sur le formulaire (« 1 », « a », « (1) »). */
  repere?: string;
  /** Phrase d'introduction que le formulaire pose au-dessus d'une série de lignes. */
  groupe?: string;
  libelle: string;
  nature: 'texte' | 'euros' | 'pourcentage' | 'stagiaires' | 'heures' | 'personnes';
  origine: 'plateforme' | 'comptabilite' | 'declaratif';
  total?: boolean;
  horsFormulaire?: boolean;
}

export function useReferentiels() {
  return useQuery({
    queryKey: ['referentiels'],
    queryFn: () => appelApi<Referentiels>('/referentiels'),
    staleTime: 30 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Bilan pédagogique et financier (CDC §4.4)
// ---------------------------------------------------------------------------

export interface ValeurBpf {
  cle: string;
  cadre: string;
  repere: string | null;
  groupe: string | null;
  libelle: string;
  /** Chaîne vide : la plateforme ne sait pas renseigner cette rubrique. */
  valeur: string;
  unite: string;
  origine: string;
  total: boolean;
  horsFormulaire: boolean;
}

export interface Bpf {
  exercice: number;
  sites: string[];
  valeurs: ValeurBpf[];
  rubriquesVides: string[];
  avertissements: string[];
}

function parametresBpf(exercice: number, site: string): string {
  return new URLSearchParams({ exercice: String(exercice), site }).toString();
}

/**
 * Aperçu du BPF avant téléchargement : l'écran doit pouvoir montrer ce que la
 * plateforme ne sait pas remplir avant que le fichier ne parte.
 */
export function useApercuBpf(exercice: number, site: string) {
  return useQuery({
    queryKey: ['bpf', exercice, site],
    queryFn: () => appelApi<Bpf>(`/documents/bpf?format=json&${parametresBpf(exercice, site)}`),
  });
}

/**
 * URL de la remontée SIFA. Même garde côté serveur que les autres documents
 * (`adm-documents`) : l'URL n'accorde rien, elle transporte le périmètre de
 * sites affiché et, le cas échéant, la formation retenue.
 */
export function urlExportSifa(site: string, formation = ''): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  const query = new URLSearchParams({ site });
  if (formation) query.set('formation', formation);
  return `${base}/documents/sifa?${query.toString()}`;
}

export function urlFichierBpf(exercice: number, site: string): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/documents/bpf?${parametresBpf(exercice, site)}`;
}

/**
 * URL du fichier d'un document produit : le navigateur porte le cookie de
 * session. La route est gardée par l'écran `adm-documents` côté serveur — un
 * appelant sans ce droit reçoit un 403, l'URL ne l'accorde pas.
 */
export function urlDocumentGenere(documentId: string): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/documents/${documentId}/fichier`;
}

/**
 * URL de l'exemplaire signé déposé en retour. Même garde côté serveur que le
 * document produit : l'URL ne l'accorde pas, elle désigne.
 */
export function urlDocumentSigne(documentId: string): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/documents/${documentId}/signe`;
}
